import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { formatCurrency, parseMonthPeriod } from '../../utils/dataUtils';
import { useCurrency } from '../../hooks/useCurrency';
import { useTheme } from '../../hooks/useTheme';
import {
  ChartEmptyState,
  getChartTheme,
  areaFill,
  tickFont,
  tooltipStyle,
  valueAxis,
  formatPeriodLabel
} from './chartTheme';

export const MonthlyChart = ({ data, showForecast = true, forecastAmount = null }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const currencyKey = useCurrency();
  const themeName = useTheme();

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Return early if no data
    if (!data || !data.labels || data.labels.length === 0) {
      return;
    }

    const ctx = chartRef.current.getContext('2d');
    const theme = getChartTheme(themeName);

    // Ensure proper date sorting for monthly data
    const sortedIndices = [...data.labels.keys()].sort((a, b) => {
      return parseMonthPeriod(data.labels[a]) - parseMonthPeriod(data.labels[b]);
    });

    const sortedLabels = sortedIndices.map(i => data.labels[i]);
    const sortedValues = sortedIndices.map(i => data.values[i]);

    // Prepare chart data: a thin line over a light wash, points ringed in the
    // surface colour so they stay readable where they sit on the line
    let chartData = {
      labels: sortedLabels,
      datasets: [{
        label: 'Monthly Spend',
        data: sortedValues,
        backgroundColor: areaFill(theme),
        borderColor: theme.primary,
        borderWidth: 2,
        tension: 0.25,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: theme.primary,
        pointBorderColor: theme.ink.surface,
        pointBorderWidth: 2
      }]
    };

    // Add forecast point if enabled
    const hasForecast = Boolean(showForecast && forecastAmount);
    if (hasForecast) {
      // Calculate the next month label after the chronologically last month
      const latestLabel = sortedLabels[sortedLabels.length - 1];
      let nextMonthLabel = '';

      if (/^\d{4}-\d{2}$/.test(latestLabel)) {
        // Format is YYYY-MM
        const year = parseInt(latestLabel.substring(0, 4));
        const month = parseInt(latestLabel.substring(5, 7));

        if (month === 12) {
          nextMonthLabel = `${year + 1}-01`;
        } else {
          nextMonthLabel = `${year}-${String(month + 1).padStart(2, '0')}`;
        }
      } else {
        // Fallback to simple labeling if we can't parse the date format
        nextMonthLabel = 'Forecast';
      }

      // Add the forecast month to the labels
      chartData.labels.push(nextMonthLabel);

      // Add the forecast value to each dataset
      chartData.datasets = chartData.datasets.map((dataset, datasetIndex) => {
        if (datasetIndex === 0) {
          // The forecast is a hollow point reached by a dashed line: the same
          // colour as the actuals, drawn as "not real yet"
          return {
            ...dataset,
            data: [...dataset.data, forecastAmount],
            pointBackgroundColor: [
              ...Array(dataset.data.length).fill(theme.primary),
              theme.ink.surface
            ],
            pointBorderColor: [
              ...Array(dataset.data.length).fill(theme.ink.surface),
              theme.primary
            ],
            pointRadius: [
              ...Array(dataset.data.length).fill(4),
              5
            ],
            segment: {
              borderDash: (ctx) => {
                const index = ctx.p0DataIndex;
                const isLastSegment = index === dataset.data.length - 1;
                return isLastSegment ? [5, 5] : undefined;
              },
              backgroundColor: (ctx) => {
                // A fainter wash under the projected segment
                const isLastSegment = ctx.p0DataIndex === dataset.data.length - 1;
                return isLastSegment ? theme.washFaint : undefined;
              }
            }
          };
        }
        return dataset;
      });
    }

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          y: valueAxis(theme),
          x: {
            border: { color: theme.ink.baseline },
            grid: { display: false },
            ticks: {
              color: theme.ink.muted,
              font: tickFont,
              padding: 6,
              maxRotation: 0,
              autoSkipPadding: 16,
              callback: function(value) {
                return formatPeriodLabel(this.getLabelForValue(value));
              }
            }
          }
        },
        plugins: {
          tooltip: {
            ...tooltipStyle(theme),
            callbacks: {
              title: items => formatPeriodLabel(items[0].label),
              label: context => {
                // The forecast, when shown, is the last point in the dataset
                const isLastPoint = context.dataIndex === context.dataset.data.length - 1;
                if (isLastPoint && hasForecast) {
                  return 'Forecast: ' + formatCurrency(context.raw);
                }
                return 'Spend: ' + formatCurrency(context.raw);
              }
            }
          },
          legend: {
            display: false // One series: the chart title names it; the forecast key is rendered below
          }
        }
      }
    });

    return () => {
      if (chartInstance.current && typeof chartInstance.current.destroy === 'function') {
        chartInstance.current.destroy();
      }
    };
  }, [data, showForecast, forecastAmount, currencyKey, themeName]);

  // Determine if we have data or need to show a message
  const hasData = data && data.labels && data.labels.length > 0;

  return (
    <div style={{ height: '100%', position: 'relative' }}>
      {!hasData ? (
        <ChartEmptyState>No monthly data available</ChartEmptyState>
      ) : (
        <canvas ref={chartRef} role="img" aria-label="Line chart of spend per month" />
      )}
    </div>
  );
};
